import { useEffect, useRef, useState } from 'react';
import { CIVS } from '../../gameplay/civilisations';
import { MAP_TYPES, type MapSize, type MapType } from '../../world/maps/mapTypes';
import { DEFAULT_GAME_SPEED, GAME_SPEEDS, PLAYER_COLORS } from '../../engine/simulation/simulation';
import { NetSession } from '../../networking/session';
import { LockstepTransport } from '../../networking/lockstep/lockstepTransport';
import { acceptInvite, completeInvite, createInvite } from '../../networking/signalling/manual';
import { WsSignalling } from '../../networking/signalling/wsSignalling';
import { HostedListing, listLobbies, roomUrl, type LobbySummary } from '../../networking/signalling/onlineLobby';
import type { RtcLink } from '../../networking/webrtc/rtcLink';
import type { LobbyState } from '../../networking/protocol';
import { useApp } from '../store';
import { ColourPicker } from './ColourPicker';

type Phase = 'choose' | 'connect' | 'lobby';
/** online: the lobby service on the game's own site; server: a self-run signalling server; manual: copy-paste codes. */
type Method = 'online' | 'server' | 'manual';
const CODE_RE = /^[A-Z]{4}$/;

const rid = () => Math.random().toString(36).slice(2, 10);

function iceServers(useStun: boolean): RTCIceServer[] {
  return useStun ? [{ urls: 'stun:stun.l.google.com:19302' }] : [];
}

export function MultiplayerLobby() {
  const { setScreen, prefs } = useApp();
  const [phase, setPhase] = useState<Phase>('choose');
  const [role, setRole] = useState<'host' | 'join'>('host');
  const [method, setMethod] = useState<Method>('online');
  const [advMethod, setAdvMethod] = useState<'server' | 'manual'>('manual');
  const [code, setCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [lobbies, setLobbies] = useState<LobbySummary[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const listing = useRef<HostedListing | null>(null);
  const [wsUrl, setWsUrl] = useState(() => `ws://${location.hostname || 'localhost'}:8787`);
  const [room, setRoom] = useState(() => Math.random().toString(36).slice(2, 7).toUpperCase());
  const [useStun, setUseStun] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [lobby, setLobby] = useState<LobbyState | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [answerIn, setAnswerIn] = useState('');
  const [inviteIn, setInviteIn] = useState('');
  const [answerOut, setAnswerOut] = useState('');
  const [busy, setBusy] = useState(false);
  const session = useRef<NetSession | null>(null);
  const ws = useRef<WsSignalling | null>(null);
  const pendingInvite = useRef<RtcLink | null>(null);
  const localId = useRef(rid());
  const supported = typeof RTCPeerConnection !== 'undefined';

  const addLog = (s: string) => setLog((l) => [...l.slice(-8), s]);

  useEffect(() => () => {
    // Leaving the lobby screen without starting: tear down.
    if (session.current && !session.current.started) session.current.close();
    // A started game owns the signalling connection (for reconnecting lost players).
    if (!session.current?.started) ws.current?.close();
    listing.current?.close();
  }, []);

  // Browse games on this network while choosing.
  useEffect(() => {
    if (phase !== 'choose') return;
    const ctrl = new AbortController();
    const poll = () =>
      listLobbies(ctrl.signal)
        .then((l) => {
          setLobbies(l);
          setListErr(null);
        })
        .catch((e) => {
          if (!ctrl.signal.aborted) setListErr(`Can't reach the lobby service, so games on your network can't be listed. ${e instanceof Error ? e.message : ''} You can still connect under "Other ways to connect".`);
        });
    void poll();
    const t = setInterval(poll, 3000);
    return () => {
      ctrl.abort();
      clearInterval(t);
    };
  }, [phase]);

  // Keep a hosted game's listing current.
  useEffect(() => {
    if (!lobby || !listing.current || !session.current?.isHost) return;
    listing.current.update({
      name: `${prefs.playerName}'s game`,
      players: lobby.slots.length,
      maxPlayers: lobby.settings.maxPlayers,
      map: MAP_TYPES.find((m) => m.key === lobby.settings.mapType)?.name ?? lobby.settings.mapType,
    });
  }, [lobby, prefs.playerName]);

  const newSession = (host: boolean, relay: boolean) => {
    const s = new NetSession(localId.current, prefs.playerName, host, relay);
    session.current = s;
    s.onLobby = (st) => setLobby({ ...st, slots: st.slots.map((x) => ({ ...x })) });
    s.onLog = addLog;
    s.onStart = (config, players, inputDelay) => {
      const me = players.indexOf(localId.current);
      // The signalling connection stays open for the game, to reconnect lost players.
      const sig = ws.current;
      if (sig) sig.onError = null;
      const transport = new LockstepTransport(s, me, players, inputDelay, sig);
      listing.current?.close();
      useApp.setState({ pendingConfig: config, launch: { transport }, screen: 'game', menuOpen: false });
    };
    setLobby(s.lobby.hostId ? { ...s.lobby } : null);
    return s;
  };

  const leave = (why?: string) => {
    session.current?.close();
    session.current = null;
    ws.current?.close();
    ws.current = null;
    listing.current?.close();
    listing.current = null;
    setLobby(null);
    setCode('');
    setLog([]);
    setError(why ?? null);
    setPhase('choose');
  };

  const connectOnline = (s: NetSession, c: string, create: boolean) => {
    // A public STUN server lets players on different networks find a route to each other.
    const sig = new WsSignalling(roomUrl(c), c, localId.current, { iceServers: iceServers(true) }, create);
    sig.onError = (e) => {
      addLog(e);
      if (!s.links.size) leave(e);
    };
    sig.onRelay = () => addLog('No direct route between you; connecting through the game server.');
    sig.onLink = (l) => {
      s.addLink(l);
      addLog(create ? 'A player connected.' : 'Connected.');
    };
    ws.current = sig;
  };

  const hostOnline = async () => {
    setBusy(true);
    setError(null);
    try {
      const l = await HostedListing.create();
      listing.current = l;
      const s = newSession(true, false);
      connectOnline(s, l.code, true);
      setMethod('online');
      setCode(l.code);
      setLobby({ ...s.lobby });
      setPhase('lobby');
    } catch (e) {
      setError(`Could not create a game: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  const joinOnline = (raw: string) => {
    const c = raw.trim().toUpperCase();
    if (!CODE_RE.test(c)) {
      setError('Game codes are four letters.');
      return;
    }
    setError(null);
    const s = newSession(false, false);
    connectOnline(s, c, false);
    setMethod('online');
    setCode(c);
    addLog(`Joining game ${c}…`);
    setPhase('lobby');
  };

  const begin = () => {
    const method = advMethod;
    setMethod(method);
    const s = new NetSession(localId.current, prefs.playerName, role === 'host', method === 'manual');
    session.current = s;
    s.onLobby = (st) => setLobby({ ...st, slots: st.slots.map((x) => ({ ...x })) });
    s.onLog = addLog;
    s.onStart = (config, players, inputDelay) => {
      const me = players.indexOf(localId.current);
      const sig = ws.current;
      if (sig) sig.onError = null;
      const transport = new LockstepTransport(s, me, players, inputDelay, sig);
      useApp.setState({ pendingConfig: config, launch: { transport }, screen: 'game', menuOpen: false });
    };
    setLobby(s.lobby.hostId ? { ...s.lobby } : null);
    if (method === 'server') {
      const sig = new WsSignalling(wsUrl, room, localId.current, { iceServers: iceServers(useStun) });
      sig.onError = addLog;
      sig.onRelay = (id) => addLog(`No direct route to ${id}; relaying through the signalling server.`);
      sig.onLink = (l) => {
        s.addLink(l);
        addLog(`Connected to peer ${l.remoteId}.`);
      };
      ws.current = sig;
      addLog(`Joined room ${room} via ${wsUrl}.`);
    }
    setPhase(method === 'server' ? 'lobby' : 'connect');
  };

  // ---------- Manual signalling ----------
  const makeInvite = async () => {
    setBusy(true);
    try {
      const { link, code } = await createInvite(localId.current, { iceServers: iceServers(useStun) });
      pendingInvite.current = link;
      setInviteCode(code);
      setAnswerIn('');
    } finally {
      setBusy(false);
    }
  };
  const finishInvite = async () => {
    const link = pendingInvite.current;
    if (!link) return;
    try {
      await completeInvite(link, answerIn);
      await link.opened;
      session.current!.addLink(link);
      addLog(`Player connected (${link.remoteId}).`);
      pendingInvite.current = null;
      setInviteCode('');
      setPhase('lobby');
    } catch (e) {
      addLog(`Failed: ${e instanceof Error ? e.message : e}`);
    }
  };
  const joinWithCode = async () => {
    setBusy(true);
    try {
      const { link, code } = await acceptInvite(localId.current, prefs.playerName, inviteIn, { iceServers: iceServers(useStun) });
      setAnswerOut(code);
      void link.opened.then(() => {
        session.current!.addLink(link);
        addLog('Connected to host.');
        setPhase('lobby');
      });
    } catch (e) {
      addLog(`Failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  const s = session.current;
  const me = lobby?.slots.find((x) => x.peerId === localId.current);
  const commitName = (sl: LobbyState['slots'][number]) => {
    const n = (nameDraft ?? '').trim().slice(0, 24);
    setNameDraft(null);
    if (!n || n === sl.name || !s) return;
    s.sendSlot(sl.civ, sl.team, sl.ready, n);
    useApp.getState().setPrefs({ playerName: n });
  };
  const isHost = !!s && s.isHost;

  return (
    <div className="menu-bg">
      <div className="menu-card">
        <div className="row spread"><h2>Multiplayer</h2><button className="btn" onClick={() => (phase === 'choose' ? setScreen('menu') : leave())}>{phase === 'choose' ? 'Back' : 'Leave'}</button></div>
        {!supported && <p style={{ color: 'var(--danger)' }}>This browser does not support WebRTC data channels.</p>}

        {phase === 'choose' && error && <p className="mp-error">{error}</p>}
        {phase === 'choose' && (
          <div className="setup-grid">
            <div className="mp-col">
              <h3>Games on your network</h3>
              {lobbies === null && !listErr && <p className="muted small">Looking for games…</p>}
              {listErr && <p className="small muted">{listErr}</p>}
              {lobbies?.length === 0 && !listErr && <p className="muted small">No games on your network yet. Host one, or join a friend's game with its code.</p>}
              {lobbies?.map((l) => (
                <div className="lobby-row" key={l.code}>
                  <div className="grow">
                    <b>{l.name}</b>
                    <div className="small muted">{l.players}/{l.maxPlayers} players · {l.map} · code {l.code}</div>
                  </div>
                  <button className="btn primary" disabled={busy || !supported || l.players >= l.maxPlayers} onClick={() => joinOnline(l.code)}>{l.players >= l.maxPlayers ? 'Full' : 'Join'}</button>
                </div>
              ))}
            </div>
            <div className="mp-col">
              <h3>Host a game</h3>
              <p className="small muted">Players on your network see it in their list straight away. Anyone else can join with its four-letter code.</p>
              <button className="btn primary" onClick={() => void hostOnline()} disabled={busy || !supported}>{busy ? 'Creating…' : 'Host game'}</button>
              <h3 style={{ marginTop: 14 }}>Join with a code</h3>
              <div className="row">
                <input className="code-input" type="text" value={joinCode} maxLength={4} placeholder="ABCD" aria-label="Game code"
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && joinCode.length === 4 && joinOnline(joinCode)} />
                <button className="btn" disabled={joinCode.length !== 4 || !supported} onClick={() => joinOnline(joinCode)}>Join</button>
              </div>
            </div>
          </div>
        )}
        {phase === 'choose' && (
          <details className="mp-advanced">
            <summary>Other ways to connect (no internet, or your own server)</summary>
          <div className="grid2">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="tabs">
                <button className={`tab ${role === 'host' ? 'active' : ''}`} onClick={() => setRole('host')}>Host a game</button>
                <button className={`tab ${role === 'join' ? 'active' : ''}`} onClick={() => setRole('join')}>Join a game</button>
              </div>
              <label className="field">Connection method
                <select value={advMethod} onChange={(e) => setAdvMethod(e.target.value as 'server' | 'manual')}>
                  <option value="manual">Copy-paste connection codes (works with no internet)</option>
                  <option value="server">Your own signalling server (npm run signal)</option>
                </select>
              </label>
              {advMethod === 'server' && (
                <>
                  <label className="field">Signalling server URL<input type="text" value={wsUrl} onChange={(e) => setWsUrl(e.target.value)} /></label>
                  <label className="field">Room code<input type="text" value={room} onChange={(e) => setRoom(e.target.value.toUpperCase().slice(0, 12))} /></label>
                </>
              )}
              <label className="small"><input type="checkbox" checked={useStun} onChange={(e) => setUseStun(e.target.checked)} /> Use public STUN server (only needed across different networks)</label>
              <button className="btn primary" disabled={!supported} onClick={begin}>{role === 'host' ? 'Create lobby' : 'Continue'}</button>
            </div>
            <div className="small muted" style={{ lineHeight: 1.6 }}>
              <p><b>Local signalling service:</b> one computer runs <code>npm run signal</code> (it also serves the game). Everyone opens the game and enters the same room code. The service only introduces peers; gameplay flows directly between browsers over WebRTC.</p>
              <p><b>Manual codes:</b> works with no server at all. The host creates an invite code for each player; the player pastes it, then sends back the reply code. Up to 3 players can join; the host relays traffic between them.</p>
              <p>All players need the same game version. Safari and Chrome can play together.</p>
            </div>
          </div>
          </details>
        )}

        {phase === 'connect' && role === 'host' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p>Create an invite code for the next player and send it to them (chat, AirDrop, etc.).</p>
            <button className="btn" onClick={makeInvite} disabled={busy}>{busy ? 'Gathering network candidates…' : 'Create invite code'}</button>
            {inviteCode && (<><textarea readOnly value={inviteCode} onFocus={(e) => e.target.select()} />
              <button className="btn" onClick={() => navigator.clipboard?.writeText(inviteCode)}>Copy invite</button>
              <label className="field">Paste their reply code<textarea value={answerIn} onChange={(e) => setAnswerIn(e.target.value)} /></label>
              <button className="btn primary" onClick={finishInvite} disabled={!answerIn}>Connect player</button></>)}
            <button className="btn" onClick={() => setPhase('lobby')}>Go to lobby</button>
          </div>
        )}
        {phase === 'connect' && role === 'join' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label className="field">Paste the host's invite code<textarea value={inviteIn} onChange={(e) => setInviteIn(e.target.value)} /></label>
            <button className="btn primary" onClick={joinWithCode} disabled={!inviteIn || busy}>{busy ? 'Working…' : 'Create reply code'}</button>
            {answerOut && (<><p>Send this reply code back to the host:</p><textarea readOnly value={answerOut} onFocus={(e) => e.target.select()} />
              <button className="btn" onClick={() => navigator.clipboard?.writeText(answerOut)}>Copy reply</button>
              <p className="muted small">Waiting for the host to connect…</p></>)}
          </div>
        )}

        {phase === 'lobby' && (
          <div className="setup-grid">
            <div>
              <h3 style={{ fontSize: 15 }}>Players {lobby ? `(${lobby.slots.length}/${lobby.settings.maxPlayers})` : ''}</h3>
              {!lobby && <p className="muted">Waiting for lobby information from the host…</p>}
              {lobby?.slots.map((sl, i) => {
                const mine = sl.peerId === localId.current;
                return (
                  <div className="player-card" key={i}>
                    <div className="line">
                      <ColourPicker value={sl.color ?? PLAYER_COLORS[i]} taken={lobby.slots.map((o, j) => o.color ?? PLAYER_COLORS[j])} disabled={!mine && !(isHost && sl.ai)}
                        onChange={(c) => (mine ? s!.sendSlot(sl.civ, sl.team, sl.ready, undefined, c) : s!.setSlot(i, { color: c }))} />
                      {mine ? (
                        <input type="text" className="grow" aria-label="Your name" maxLength={24} value={nameDraft ?? sl.name}
                          onChange={(e) => setNameDraft(e.target.value)}
                          onBlur={() => commitName(sl)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
                      ) : (
                        <span className="grow">{sl.name}{sl.peerId === lobby.hostId ? ' (host)' : ''}{sl.ai ? ` · AI ${sl.ai.difficulty}` : ''}</span>
                      )}
                      <span className="small" style={{ color: sl.ready ? 'var(--good)' : 'var(--muted)' }}>{sl.ready ? 'Ready' : 'Not ready'}</span>
                      {isHost && !mine ? <button className="btn" onClick={() => s!.removeSlot(i)} aria-label="Remove player">×</button> : <span className="btn-spacer" />}
                    </div>
                    <div className="line sub">
                      <select className="grow" value={sl.civ} disabled={!mine && !(isHost && sl.ai)} onChange={(e) => (mine ? s!.sendSlot(e.target.value, sl.team, sl.ready) : s!.setSlot(i, { civ: e.target.value }))}>
                        {CIVS.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                      </select>
                      <select value={sl.team} disabled={!mine && !(isHost && sl.ai)} onChange={(e) => (mine ? s!.sendSlot(sl.civ, Number(e.target.value), sl.ready) : s!.setSlot(i, { team: Number(e.target.value) }))}>
                        <option value={-1}>Free for all</option>{[1, 2, 3, 4].map((t) => <option key={t} value={t}>Team {t}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
              <div className="row" style={{ marginTop: 10 }}>
                {me && !isHost && <button className="btn" onClick={() => s!.sendSlot(me.civ, me.team, !me.ready)}>{me.ready ? 'Not ready' : 'Ready'}</button>}
                {isHost && <button className="btn" onClick={() => s!.addAI()}>+ AI player</button>}
                {isHost && method === 'manual' && <button className="btn" onClick={() => setPhase('connect')}>Invite another player</button>}
              </div>
              <div className="small muted" style={{ marginTop: 12 }}>{log.map((l, i) => <div key={i}>{l}</div>)}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h3 style={{ fontSize: 15 }}>Settings</h3>
              {lobby && (<>
                <label className="field">Map type
                  <select disabled={!isHost} value={lobby.settings.mapType} onChange={(e) => s!.updateSettings({ mapType: e.target.value as MapType })}>
                    {MAP_TYPES.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
                  </select>
                </label>
                <label className="field">Map size
                  <select disabled={!isHost} value={lobby.settings.mapSize} onChange={(e) => s!.updateSettings({ mapSize: e.target.value as MapSize })}>
                    {['small', 'medium', 'large', 'huge'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="field">Seed<input type="number" disabled={!isHost} value={lobby.settings.seed} onChange={(e) => s!.updateSettings({ seed: Number(e.target.value) || 0 })} /></label>
                <label className="field">Game speed
                  <select disabled={!isHost} value={lobby.settings.gameSpeed ?? DEFAULT_GAME_SPEED} onChange={(e) => s!.updateSettings({ gameSpeed: Number(e.target.value) })}>
                    {GAME_SPEEDS.map((g) => <option key={g.value} value={g.value}>{g.name}</option>)}
                  </select>
                </label>
                <label className="field">Max players
                  <select disabled={!isHost} value={lobby.settings.maxPlayers} onChange={(e) => s!.updateSettings({ maxPlayers: Number(e.target.value) })}>
                    {[2, 3, 4].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
              </>)}
              {method === 'online' && code && (
                <div className="game-code">
                  <span className="small muted">Game code</span>
                  <b>{code}</b>
                  <span className="small muted">Players on your network see this game in their list. Others can join by typing the code.</span>
                </div>
              )}
              {method === 'server' && <p className="small muted">Room code: <b style={{ color: 'var(--gold-bright)' }}>{room}</b></p>}
              {isHost && <button className="btn primary" disabled={!!s?.canStart()} onClick={() => s!.start()}>Start match</button>}
              {isHost && s?.canStart() && <span className="small muted">{s.canStart()}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
